import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { extractErrorMessage } from '@/hooks/useErrorHandler';

interface LocationState {
  from?: { pathname: string };
}

export const useLoginForm = () => {
  const { login, isLoading } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    try {
      await login(email, password);
      // Success toast is a quiet confirmation — the immediate navigate
      // away from /login is the primary feedback, the toast just bridges
      // the small route-loading gap so the user knows "yes, signed in".
      toast.success('Signed in.');
      const from = (location.state as LocationState)?.from?.pathname ?? '/devices';
      navigate(from, { replace: true });
    } catch (err) {
      // Keep inline form error AND toast — inline is the primary
      // affordance (shows next to the form), toast covers the case
      // where the form scrolled off-screen on a small viewport.
      const msg = extractErrorMessage(err);
      setLocalError(msg);
      toast.error(msg || 'Sign-in failed.');
    }
  };

  return {
    email, setEmail,
    password, setPassword,
    isLoading,
    error: localError,
    onSubmit,
  };
};
